import { useEffect, useRef, useState } from "react";

import { FAB_SIZE, type FabPosition } from "../useFabPosition";
import NexaMark from "./NexaMark";

interface NexaFabProps {
  onOpen: () => void;
  position: FabPosition;
  dragging: boolean;
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => boolean;
}

const NexaFab = ({ onOpen, position, dragging, onPointerDown, onPointerMove, onPointerUp }: NexaFabProps) => {
  const [showTip, setShowTip] = useState(false);
  const [hovered, setHovered] = useState(false);
  const tipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => void (tipTimer.current && clearTimeout(tipTimer.current)), []);

  useEffect(() => {
    if (!dragging) return;
    setShowTip(false);
    if (tipTimer.current) clearTimeout(tipTimer.current);
  }, [dragging]);

  const enter = () => {
    setHovered(true);
    tipTimer.current = setTimeout(() => setShowTip(true), 650);
  };
  const leave = () => {
    if (tipTimer.current) clearTimeout(tipTimer.current);
    setHovered(false);
    setShowTip(false);
  };

  const tipOnRight = position.x < 220;

  return (
    <div className="tw-fixed tw-z-[55]" style={{ left: position.x, top: position.y, width: FAB_SIZE, height: FAB_SIZE }}>
      {showTip && <Tooltip onRight={tipOnRight} />}

      <button
        type="button"
        aria-label="Open Nexa AI"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => {
          if (onPointerUp(e)) onOpen();
        }}
        onMouseEnter={enter}
        onMouseLeave={leave}
        className="tw-relative tw-grid tw-place-items-center tw-rounded-full tw-border tw-border-[#E7E5F2] tw-bg-white tw-touch-none"
        style={{
          width: FAB_SIZE,
          height: FAB_SIZE,
          cursor: dragging ? "grabbing" : "grab",
          transform: dragging ? "scale(1.04)" : hovered ? "scale(1.07)" : "scale(1)",
          transition: dragging ? "none" : "transform .18s cubic-bezier(.2,.8,.3,1), box-shadow .18s ease",
          boxShadow: dragging
            ? "0 6px 14px rgba(30,27,75,.18), 0 22px 44px rgba(84,72,200,.34)"
            : hovered
              ? "0 2px 5px rgba(30,27,75,.10), 0 14px 32px rgba(84,72,200,.28)"
              : "0 1px 3px rgba(30,27,75,.08), 0 8px 22px rgba(30,27,75,.16)",
        }}
      >
        <span className="tw-pointer-events-none tw-absolute tw-inset-1 tw-rounded-full" style={{ background: "radial-gradient(circle at 50% 30%, #F6F4FF 0%, #FFFFFF 70%)" }} />
        <span className="tw-pointer-events-none tw-relative">
          <NexaMark size={32} />
        </span>
      </button>

    </div>
  );
};

const Tooltip = ({ onRight }: { onRight: boolean }) => (
  <div
    className={`tw-pointer-events-none tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-whitespace-nowrap tw-rounded-lg tw-bg-[#1E1B4B] tw-px-3 tw-py-2 tw-text-label-sm tw-font-medium tw-text-white tw-shadow-lg ${
      onRight ? "tw-left-full tw-ml-2.5" : "tw-right-full tw-mr-2.5"
    }`}
  >
    Ask Nexa AI
  </div>
);

export default NexaFab;
